import { offlineDb, scopedKey } from './offline-db';
import { isOfflineCapableNative } from '../commons/utils/platform';

// Transport-unit offline window (OFFLINE_PLAN.md Phase 2 / D7, 2026-09-29).
//
// What the phone keeps offline beyond the newest-10 list: every transport unit created in the last 90 days,
// every still-open one, and every one the user pinned with "Keep offline" — as list rows (same shape as
// /transportunits/paginated), so the Transport Units screen can search / sort / page them offline, plus each
// unit's own detail + tally rows (warmed by OfflinePrefetchService). Photos/documents of these units are
// NOT downloaded ahead — only when opened (the newest-10 still get theirs, as before).
//
// Stored in masterCache under tenant-scoped keys: a read cache, so a different user/company logging in
// wipes it (pins included — pins belong to the person who made them).

const WINDOW_KEY = 'tuOfflineWindow';
const MARKS_KEY = 'tuOfflineWindowMarks';
const PINS_KEY = 'tuOfflinePins';

export const TU_WINDOW_DAYS = 90;
export const TU_WINDOW_MAX = 300;

export interface TuWindow {
  rows: any[];
  fetchedAt: number;
}

export interface TuPin {
  id: number;
  encodedId: string;
}

export async function getTuWindow(): Promise<TuWindow | null> {
  if (!isOfflineCapableNative()) return null;
  const entry = await offlineDb.masterCache.get(scopedKey(WINDOW_KEY));
  const data = entry?.data as TuWindow | undefined;
  return data && Array.isArray(data.rows) ? data : null;
}

export async function saveTuWindow(rows: any[]): Promise<void> {
  const fetchedAt = Date.now();
  await offlineDb.masterCache.put({ key: scopedKey(WINDOW_KEY), data: { rows, fetchedAt } as TuWindow, fetchedAt });
}

/** "Has this unit changed since its offline copy was saved?" — the unit's own last change plus its totals
 *  and file counts (a tally row edit changes the totals without touching the unit itself). */
export function tuFingerprint(row: any): string {
  return [row?.lastModifiedDate, row?.totalPieces, row?.netVolumeCbm, row?.photoCount, row?.fileCount].join('|');
}

export async function getTuWindowMarks(): Promise<Record<string, string>> {
  const entry = await offlineDb.masterCache.get(scopedKey(MARKS_KEY));
  return (entry?.data as Record<string, string>) ?? {};
}

export async function saveTuWindowMarks(marks: Record<string, string>): Promise<void> {
  await offlineDb.masterCache.put({ key: scopedKey(MARKS_KEY), data: marks, fetchedAt: Date.now() });
}

export async function getTuPins(): Promise<TuPin[]> {
  if (!isOfflineCapableNative()) return [];
  const entry = await offlineDb.masterCache.get(scopedKey(PINS_KEY));
  return Array.isArray(entry?.data) ? (entry!.data as TuPin[]) : [];
}

/** Pin / unpin one transport unit for offline use. Returns the new pin list. */
export async function setTuPinned(row: { id: number; encodedId: string }, pinned: boolean): Promise<TuPin[]> {
  const pins = (await getTuPins()).filter((p) => p.id !== row.id);
  if (pinned && row.id > 0 && row.encodedId) pins.push({ id: row.id, encodedId: row.encodedId });
  await offlineDb.masterCache.put({ key: scopedKey(PINS_KEY), data: pins, fetchedAt: Date.now() });
  return pins;
}

// Offline search over the window — the same kinds of fields the server's globalFilter matches.
const SEARCH_FIELDS = [
  'transportId', 'stockUnitId', 'unitNumber', 'sealNumber', 'supplierName', 'buyerName', 'productName',
  'species', 'loadingSiteName', 'transportModeLabel', 'countryOfOriginName', 'seaPortName',
];

/** Search / direction filter / sort / page over the window rows, like /transportunits/paginated does online. */
export function queryTuWindow(
  rows: any[],
  opts: { page: number; size: number; sortBy: string; sortDir: 'asc' | 'desc'; filter: string; direction: string },
): { content: any[]; total: number } {
  return queryRows(rows, {
    ...opts,
    sortBy: opts.sortBy || 'createdDate',
    fields: SEARCH_FIELDS,
    keep: (r) => !opts.direction || r?.transactionType === opts.direction,
  });
}

// ── Full lists for the Business Partners / Products / Loading Sites screens (Phase 2 / D7) ─────────────
// The dropdown lists were already complete offline; the list SCREENS only had their first 10 rows. The
// refresh now also saves the whole list (one request, up to FULL_LIST_MAX rows) and each screen answers
// offline search / sort / paging from it.
export type FullListName = 'businessPartners' | 'products' | 'loadingSites';
export const FULL_LIST_MAX = 1000;

export async function saveFullList(name: FullListName, rows: any[]): Promise<void> {
  if (!isOfflineCapableNative()) return;
  const fetchedAt = Date.now();
  await offlineDb.masterCache.put({ key: scopedKey(`fullList:${name}`), data: { rows, fetchedAt } as TuWindow, fetchedAt });
}

export async function getFullList(name: FullListName): Promise<TuWindow | null> {
  if (!isOfflineCapableNative()) return null;
  const entry = await offlineDb.masterCache.get(scopedKey(`fullList:${name}`));
  const data = entry?.data as TuWindow | undefined;
  return data && Array.isArray(data.rows) ? data : null;
}

/** Generic local query: keep → search (any of `fields`, case-insensitive, nested a.b allowed) → sort → page. */
export function queryRows(
  rows: any[],
  opts: { page: number; size: number; sortBy: string; sortDir: 'asc' | 'desc'; filter: string; fields: string[]; keep?: (r: any) => boolean },
): { content: any[]; total: number } {
  const q = (opts.filter || '').trim().toLowerCase();
  const read = (r: any, path: string) => path.split('.').reduce((v: any, k) => (v == null ? v : v[k]), r);
  const filtered = rows.filter((r) =>
    (!opts.keep || opts.keep(r))
    && (!q || opts.fields.some((f) => {
      const v = read(r, f);
      return (Array.isArray(v) ? v.join(' ') : String(v ?? '')).toLowerCase().includes(q);
    })));
  const dir = opts.sortDir === 'asc' ? 1 : -1;
  const field = opts.sortBy || 'name';
  filtered.sort((a, b) => {
    const x = read(a, field);
    const y = read(b, field);
    if (x == null && y == null) return 0;
    if (x == null) return 1;
    if (y == null) return -1;
    if (typeof x === 'number' && typeof y === 'number') return (x - y) * dir;
    return String(x).localeCompare(String(y), undefined, { numeric: true, sensitivity: 'base' }) * dir;
  });
  const start = Math.max(0, opts.page) * opts.size;
  return { content: filtered.slice(start, start + opts.size), total: filtered.length };
}
